import React, { useEffect, useState } from 'react'

import BEMHelper from '@ynap/bem-helper'
import { ToggleSection } from '@ynap/toggle'
import { formatDate } from '@ynap/intl-formatters'

import VerticalTimelineSkeleton from './skeleton/VerticalTimelineSkeleton'
import HeaderSkeleton from './skeleton/HeaderSkeleton'
import DeliveryTime from './DeliveryTime/DeliveryTime'
import PrimaryStatus from './PrimaryStatus/PrimaryStatus'
import PartialReturnAlert from './PartialReturnAlert'
import { trackOrderDetailsTracking } from './analytics'
import { Button } from '@ynap/button'
import Skeleton from '@ynap/skeleton'
import { IUseOrderTrackingHook } from '../../../utils/order-status/interfaces'

const bem = new BEMHelper('OrderTracking')
const timelineItemBem = new BEMHelper('TimelineItem')

type OrderTrackingInfoProps = {
    orderDateRaw: string
    externalOrderId: string
    orderStatus: string
    activeOrderStatus: string
    courierTrackingId: string
    timeDeliveredRaw: string
    dateShippedRaw: string
    messages: any
    shippingAddress: {
        country: string
    }
    tracking: {
        getTrackingData: () => void
        trackEvent: (event: any) => void
    }
    courierTrackingUrl: string
    shippingMethod: Array<string>
    locale: {
        country: string
        currency: string
        id: string
        langISO: string
        language: string
    }
    guestUser: boolean
    title: string
    subTitle: string
    trackingData: IUseOrderTrackingHook
    isPartialReturnRefused: boolean
    type: "order" | "return",
    isOrderDetailsFetched: boolean
    courierTrackingMessage: string
}

function OrderTrackingInfo(props: OrderTrackingInfoProps) {
    const {
        title,
        subTitle,
        orderDateRaw,
        externalOrderId,
        messages,
        tracking,
        courierTrackingUrl,
        locale,
        trackingData,
        isPartialReturnRefused,
        type,
        isOrderDetailsFetched,
        courierTrackingMessage
    } = props
    const {
        orderPrimaryStatus,
        orderTrackingStatus,
        orderTrackingData,
        orderEstimatedDeliveryDate,
        orderDeliveryDate,
        courier,
        isFinalStatus,
    } = trackingData
    const [toggle, setToggle] = useState<string>('')

    useEffect(() => {
        const primaryStatus = orderPrimaryStatus.status

        if (isFinalStatus && primaryStatus) {
            setToggle(primaryStatus)

            const events = orderTrackingData?.find((obj) => obj.status === primaryStatus)?.events

            tracking?.trackEvent(
                trackOrderDetailsTracking([
                    {
                        externalOrderId,
                        orderPrimaryStatus: primaryStatus,
                        orderTrackingStatus: primaryStatus !== orderTrackingStatus ? orderTrackingStatus : '',
                        orderDateRaw,
                        orderEstimatedDeliveryDate,
                        courier,
                        currentLocation: events?.[events.length - 1]?.location || '',
                    },
                ], type)
            )
        }
    }, [isFinalStatus, orderPrimaryStatus.status])

    const activeIdx = orderPrimaryStatus && orderTrackingData?.findIndex((s) => s.status === orderPrimaryStatus.status)

    return (
        <div className={bem(null)}>
            <div className={bem('header')}>
                {isOrderDetailsFetched ? (
                    <div className={bem('headerContainer')}>
                        <div className={bem('header-hero')}>
                            <h1 className={bem('title')}>{title}</h1>
                            <p className={bem('subTitle')}>{subTitle}</p>
                        </div>
                        {orderPrimaryStatus.status && isFinalStatus ? (
                            <div className={bem('header-status')}>
                                <div className={bem('orderId')}>
                                    <span>{messages.orderTracking.order({ orderId: externalOrderId })}</span>
                                </div>
                                <PrimaryStatus orderPrimaryStatus={orderPrimaryStatus} messages={messages} />
                                {isPartialReturnRefused && <PartialReturnAlert messages={messages} />}
                                <DeliveryTime
                                    orderPrimaryStatus={orderPrimaryStatus}
                                    orderEstimatedDeliveryDate={orderEstimatedDeliveryDate}
                                    orderDeliveryDate={orderDeliveryDate}
                                    messages={messages}
                                    locale={locale}
                                    type={type}
                                />
                            </div>
                        ): (
                            <div className={bem('header-status')}>
                                <Skeleton className={bem('skeleton-title')} />
                                <Skeleton className={bem('skeleton-description')} />
                            </div>
                        )}
                    </div>
                ) : (
                    <HeaderSkeleton />
                )}
            </div>
            {isOrderDetailsFetched ? (
                <ul className={bem('timelineWrapper')}>
                    {orderTrackingData.map(({ status, title, events }, i) => {
                        const active = activeIdx === i
                        const open = status === toggle
                        const haveEvents = events.length > 0
                        let statusModifier = 'disabled'
                        if (activeIdx >= i) statusModifier = 'complete'
                        if (open) statusModifier = 'open'

                        return (
                            <ToggleSection
                                className={timelineItemBem('', [
                                    statusModifier,
                                    active ? 'active' : '',
                                    !haveEvents ? 'noEvents' : '',
                                ])}
                                title={title}
                                onToggle={
                                    statusModifier !== 'disabled' && haveEvents
                                        ? () => (open ? setToggle('') : setToggle(status))
                                        : () => {}
                                }
                                open={open}
                                key={status}
                                headerClassName={timelineItemBem('header')}
                            >
                                {
                                    <div className={bem('events')}>
                                        {events.map(({ status, description, date, location }, idx) => {

                                            // gets the index that matches current order status
                                            const eventIdx = events
                                                .map((s) => s.status === orderTrackingStatus)
                                                .lastIndexOf(true)
                                            
                                            // Check if date doesn't have a timeZone
                                            const noTimeZone = date && (/\d{4}-\d{2}-\d{2}$/).test(date);

                                            return (
                                                // if event id is less than current order status or step has been completed
                                                <div
                                                    key={`${status}-${idx}`}
                                                    className={timelineItemBem(
                                                        'event',
                                                        idx <= eventIdx || activeIdx > i ? 'complete' : 'disabled'
                                                    )}
                                                >
                                                    {description && <p className={timelineItemBem('eventLine')}>{description}</p>}
                                                    {date && <p className={timelineItemBem('eventLine')}>
                                                        {formatDate(
                                                            new Date(date),
                                                            {
                                                                weekday: 'long',
                                                                year: 'numeric',
                                                                month: 'long',
                                                                day: 'numeric',
                                                                ...(noTimeZone && { timeZone: "UTC" }),
                                                            },
                                                            locale.id
                                                        )}
                                                    </p>}
                                                    {location && <p className={timelineItemBem('eventLine')}>{location}</p>}
                                                </div>
                                            )
                                        })}
                                    </div>
                                }
                            </ToggleSection>
                        )
                    })}
                </ul>
            ) : (
                <VerticalTimelineSkeleton />
            )}
            {!!courierTrackingUrl && (
                <div className={bem('trackingLink')}>
                    <p>
                        {courierTrackingMessage}
                        <Button
                            type="primary-link"
                            component="a"
                            href={courierTrackingUrl}
                            target="_blank"
                            rel="noreferrer noopener"
                        >
                            {messages.orderTracking.trackingLink()}
                        </Button>
                    </p>
                </div>
            )}
        </div>
    )
}

export default OrderTrackingInfo